import { ScrollArea } from '@/components/ui/ScrollArea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { cn } from '@/lib/utils';

import {
  CONTENT_WITHHELD_LABEL,
  type McpCallListItem,
} from '../mcp-info.types';

import { McpInfoStatusBadge } from './mcp-info-status-badge';

const COLUMNS = ['Time', 'Client', 'Tool', 'Status', 'Latency', 'Arguments'];

function formatTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleString('en-US', { hour12: false });
}

interface McpInfoCallTableProps {
  calls: McpCallListItem[];
  isLoading: boolean;
  selectedCallId: string | null;
  onSelect: (callId: string | null) => void;
}

export function McpInfoCallTable({
  calls,
  isLoading,
  selectedCallId,
  onSelect,
}: McpInfoCallTableProps) {
  return (
    <ScrollArea className="h-full">
      <Table>
        <TableHeader className="border-b border-border bg-surface sticky top-0 z-10">
          <TableRow className="border-none">
            {COLUMNS.map((column) => (
              <TableHead
                key={column}
                className="h-7 px-2 text-xs text-foreground-subtle"
              >
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className="text-center py-12">
                <span className="text-sm text-foreground-muted">
                  {isLoading
                    ? 'Loading MCP calls...'
                    : 'No MCP calls in this window.'}
                </span>
              </TableCell>
            </TableRow>
          ) : (
            calls.map((call) => {
              const isSelected = call.id === selectedCallId;

              return (
                <TableRow
                  key={call.id}
                  onClick={() => onSelect(isSelected ? null : call.id)}
                  className={cn(
                    'h-7 cursor-pointer border-border hover:bg-surface-hover',
                    isSelected &&
                      'bg-secondary-active hover:bg-secondary-active',
                    call.status === 'error' && 'border-l-2 border-l-danger-9'
                  )}
                >
                  <TableCell className="py-0 px-2 text-xs font-mono text-foreground-muted tabular-nums whitespace-nowrap">
                    {formatTime(call.created_at)}
                  </TableCell>
                  <TableCell className="py-0 px-2 text-xs text-foreground truncate max-w-[140px]">
                    {call.client_name || '-'}
                  </TableCell>
                  <TableCell className="py-0 px-2 text-xs text-foreground truncate max-w-[180px]">
                    {call.tool_name}
                  </TableCell>
                  <TableCell className="py-0 px-2">
                    <McpInfoStatusBadge status={call.status} />
                  </TableCell>
                  <TableCell className="py-0 px-2 text-xs font-mono text-foreground-muted tabular-nums whitespace-nowrap">
                    {call.execution_time_ms === null
                      ? '-'
                      : `${call.execution_time_ms}ms`}
                  </TableCell>
                  <TableCell className="py-0 px-2 text-xs font-mono text-foreground-subtle truncate max-w-[260px]">
                    {call.content_withheld
                      ? CONTENT_WITHHELD_LABEL
                      : call.arguments_preview}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}
